import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
	ComponentTypeSK,
	dottedName,
	ListingSummary,
	PermissionLevel,
	TeamMember,
	UiArguments,
	UiComponent,
	UiSearchService,
} from "@recore-solutions/nstar-lib";
import { of } from "rxjs";
import { map } from "rxjs/operators";
import { CodeTableService } from "src/app/listing/code-table.service";
import { customSortOrder } from "../code-table-sort-order";
import { ListingService } from "./listing.service";
import { UntypedFormGroup } from "@angular/forms";

type FnFactories = UiSearchService["fnFactories"];

@Injectable({ providedIn: "root" })
export class LoonFnFactoriesService implements FnFactories {
	constructor(
		protected listingService: ListingService,
		protected codeTableService?: CodeTableService
	) {}

	get factories(): FnFactories {
		return {
			queryTransformFn: this.queryTransformFn,
			searchFn: this.searchFn,
			resultFindFn: this.resultFindFn,
			resultTransformFn: this.resultTransformFn,
		};
	}

	queryTransformFn = (
		uiArguments: UiArguments
	): ((term: any, additionalParams?: any[]) => HttpParams | null) => {
		const parent = this.getParentFromUiArguments(uiArguments);
		const component = this.getPrimaryComponent(uiArguments);
		const children = this.getChildrenFromUiArguments(uiArguments);
		const reduceToNameAndValuePairs = this.reduceToNameAndValuePairs(uiArguments, children);

		if (!children?.length) {
			return () => new HttpParams();
		}

		const dependent = children.map(this.getDependentOf(uiArguments)).filter((u) => !!u);
		const componentDottedName = dottedName(component);
		const alternativeProperty = component.longName || componentDottedName;

		return (term: any) => {
			if (parent.componentTypeSK === ComponentTypeSK.SearchSelect && !term) {
				return null;
			}
			let nameAndValuePairs: { [name: string]: any } = {};
			if (!term || typeof term === "string") {
				nameAndValuePairs = dependent.reduce(reduceToNameAndValuePairs, nameAndValuePairs);
			}
			if (this.termIsDefined(term)) {
				const propertyName = typeof term === "string" ? alternativeProperty : componentDottedName;
				nameAndValuePairs[propertyName] = this.getFormattedTerm(term, propertyName);
			}
			return this.getHttpParamsFromNameAndValuePairs(nameAndValuePairs);
		};
	};

	searchFn = (uiArguments: UiArguments) => {
		const component = this.getPrimaryComponent(uiArguments);
		return (queryObj: HttpParams) => {
			if (!queryObj) {
				return of([]);
			}
			// If there is a query string, or if this is a request that we want to bypass the code table batching, then use the old approach.
			if (this.makeIndividualRequest(queryObj, component)) {
				return this.listingService
					.getPropertyTypeCodeTableValues(
						component.schemaName,
						component.databaseTable,
						queryObj,
						uiArguments.listingSummary
					)
					.pipe(map((typeCodes) => customSortOrder(typeCodes, component.databaseTable)));
			}
			// Batch this request.

			return this.codeTableService.requestTable(
				component.databaseTable,
				component.schemaName,
				uiArguments.listingSummary,
				component
			);
		};
	};

	resultFindFn = (uiArguments: UiArguments) => (value) => (result) => {
		value = value?.toJSON?.() || value;
		return result?.[uiArguments?.uiComponent?.applicationPropertyName] === value;
	};

	resultTransformFn = (_: UiArguments) => (results: any[]) => results;

	protected reduceToNameAndValuePairs =
		(uiArguments: UiArguments, children: UiComponent<any>[]) =>
		(a: { [key: string]: any }, c: UiComponent<any>, i: number) => {
			const control = uiArguments.formGroup?.getColumnByUiComponent(c, uiArguments.keys);
			return !!control?.value
				? {
						...a,
						[dottedName(children[i])]: control.value?.key
							? control.value.key
							: control.value.toJSON
							? control.value.toJSON()
							: control.value,
				  }
				: a;
		};

	protected reduceAdditionalParams = (
		children: UiComponent<any>[],
		additionalParams: any[],
		httpParams?: HttpParams
	) => {
		httpParams = httpParams || new HttpParams();
		return children.reduce(
			(a, c, i) =>
				!Array.isArray(additionalParams[i])
					? a.set(
							c.databaseTable + "." + c.databaseColumn,
							additionalParams[i]?.key ? additionalParams[i].key : additionalParams[i]
					  )
					: a,
			httpParams
		);
	};

	protected queryTransformByIdFn = (
		term: number,
		databaseTable: string,
		databaseColumn: string
	): HttpParams => {
		return new HttpParams({
			fromObject: {
				[databaseTable + "." + databaseColumn]: String(term),
			},
		});
	};

	protected termIsDefined = (term: string | { key: any }): boolean => {
		return term !== null && term !== undefined && (typeof term !== "object" || !!term.key);
	};

	protected getFormattedTerm = (term: any, propertyName: string, prefixDelimiter = false) => {
		const delimiter = propertyName.indexOf("FullTextSearch") > -1 ? "~" : "%";
		let formattedTerm = term;
		if (typeof term === "string") {
			if (prefixDelimiter) {
				formattedTerm = delimiter + term.trim();
			} else {
				formattedTerm = delimiter + term.trim().split(" ").join(delimiter) + delimiter;
			}
		}
		if (!!term.key) {
			formattedTerm = term.key;
		}
		return formattedTerm;
	};

	protected getHttpParamsFromNameAndValuePairs = (obj: {
		[key: string]: any;
	}): HttpParams | null => {
		if (!Object.keys(obj)?.length) {
			return null;
		}
		return new HttpParams({ fromObject: { ...obj } });
	};

	protected getListingSummaryExtract = (
		formGroup: UntypedFormGroup
	): Pick<
		ListingSummary,
		"listingSK" | "listingID" | "listingOrganizationSK" | "propertyTypeCD" | "listingStatusCD"
	> => {
		const listing = formGroup?.getRawValue()?.Listing?.[0];
		const listingSK = listing?.listingSK;
		const listingID = listing?.listingID;
		const listingOrganizationSK = listing?.listingOrganizationSK;
		const propertyTypeCD = listing?.propertyTypeCD;
		const listingStatusCD =
			typeof listing?.listingStatusCD !== "string"
				? listing?.listingStatusCD.toJSON()
				: listing?.listingStatusCD;
		return {
			listingSK,
			listingID,
			listingOrganizationSK,
			propertyTypeCD,
			listingStatusCD,
		};
	};

	protected makeIndividualRequest = (
		queryObj: HttpParams,
		uiComponent: UiComponent<any>
	): boolean => {
		return !!queryObj.toString() || this.skipCodeTable(uiComponent.databaseTable);
	};

	protected getPrimaryComponent = (uiArguments: UiArguments): UiComponent<any> => {
		const parent = this.getParentFromUiArguments(uiArguments);
		return [
			ComponentTypeSK.SingleSelect,
			ComponentTypeSK.MultiSelect,
			ComponentTypeSK.RadioButton,
			ComponentTypeSK.SearchSelect,
			ComponentTypeSK.Unit,
		].indexOf(parent.componentTypeSK) > -1
			? uiArguments.uiComponent
			: parent;
	};

	getParentFromUiArguments = (uiArguments: UiArguments): UiComponent<any> => {
		return uiArguments.uiComponents.find(
			(u) => u.applicationUIComponentSK === uiArguments.uiComponent.parentApplicationUIComponentSK
		);
	};

	protected getChildrenFromUiArguments = (uiArguments: UiArguments): UiComponent<any>[] => {
		return uiArguments.uiComponents.filter(
			(u) => u.parentApplicationUIComponentSK === uiArguments.uiComponent.applicationUIComponentSK
		);
	};

	protected getDependentOf = (uiArguments: UiArguments) => (component: UiComponent<any>) => {
		return uiArguments.uiComponents.find(
			(u) => u.applicationUIComponentSK === component.dependentApplicationUIComponentSK
		);
	};

	protected skipCodeTable = (tableName: string): boolean => {
		return !tableName.includes("Code") && !["MLSAreaMajor"].includes(tableName);
	};

	protected genericSearchFn = (procName: string) => (httpParams: HttpParams) => {
		if (!httpParams) {
			return of([]);
		}
		return this.listingService.getGenericProcValues(procName, httpParams);
	};

	mapPermissionLevelsToPermissionLevel = (
		permissionLevels: PermissionLevel[],
		organizationSK: number
	): PermissionLevel[] => {
		const superPermissionLevel = permissionLevels.find((pl) => pl.accessName === "Super User");
		return !!superPermissionLevel
			? [].concat(superPermissionLevel)
			: permissionLevels.filter((pl) => pl.organizationSK === organizationSK);
	};

	getSearchMembers = (httpParams: HttpParams) => {
		return this.listingService
			.getSearchMember(httpParams)
			.pipe(map((members) => members.map((m) => ({ ...m, _updateRelated: true }))));
	};

	getMembers = (httpParams: HttpParams) => {
		return this.listingService.getMember(httpParams);
	};

	getTeamMembers = (query: HttpParams) => {
		return this.listingService
			.getGenericProcValues<TeamMember>("Roster.spGetTeamMemberAPI", query)
			.pipe(
				map((members) =>
					(members || []).filter(
						(m, i) => members.findIndex((mm) => mm.memberSK === m.memberSK) === i
					)
				),
				map((members) => members.map((m) => ({ ...m, _updateRelated: true })))
			);
	};

	getTeams = (query: HttpParams) => {
		return this.listingService.getGenericProcValues<TeamMember>("Roster.spGetTeamMemberAPI", query);
	};
}
